import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import * as services from "../services/customer";
import Swal from "sweetalert2";
import provinces from "../provinces.json";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ClassIcon from "@mui/icons-material/Class";

const CarFormDialog = ({
  open,
  onClose,
  onSuccess,
  customerData,
  initialData,
}) => {
  const isEdit = false;

  const [carType, setCarType] = useState(initialData?.type || "");
  const [carLicense, setCarLicense] = useState(initialData?.license || "");
  const [province, setProvince] = useState(initialData?.province || "");
  const [saving, setSaving] = useState(false);

  // เก็บข้อความ error รายช่อง
  const [errors, setErrors] = useState({
    carType: "",
    carLicense: "",
    province: "",
  });

  const resetForm = () => {
    setCarType("");
    setCarLicense("");
    setProvince("");
    setErrors({ carType: "", carLicense: "", province: "" });
    setSaving(false);
  };

  // provinces ให้ชัวร์ว่าเป็น array
  const provinceList = useMemo(() => {
    if (Array.isArray(provinces)) return provinces;
    if (provinces && typeof provinces === "object")
      return Object.values(provinces);
    return [];
  }, []);

  // canSubmit: ฟอร์มครบ + ทะเบียน ≥ 4 หลัก
  const canSubmit = useMemo(() => {
    const isWalking = carType.trim() === "เดินมา";

    if (isWalking) {
      // เดินมา → ให้ผ่านทันที
      return true;
    }

    const license = carLicense || "";

    // เงื่อนไขทั่วไป
    return Boolean(carType.trim() && province.trim() && license.length >= 4);
  }, [carType, carLicense, province]);

  // validate ทั้งฟอร์ม
  const validate = () => {
    const next = { carType: "", carLicense: "", province: "" };
    let ok = true;

    const isWalking = carType.trim() === "เดินมา";

    // --- validate carType ---
    if (!carType.trim()) {
      next.carType = "กรุณาเลือกประเภทรถ";
      ok = false;
    }

    // --- ถ้าเป็น "เดินมา" → ข้ามการ validate อย่างอื่น ---
    if (isWalking) {
      setErrors(next);
      return {
        ok,
        firstError: next.carType || "",
      };
    }

    // --- validate carLicense ---
    const licenseDigits = (carLicense || "").replace(/\D/g, "");
    if (!licenseDigits) {
      next.carLicense = "กรุณากรอกทะเบียนรถ";
      ok = false;
    } else if (licenseDigits.length < 4) {
      next.carLicense = "ทะเบียนรถต้องมีอย่างน้อย 4 หลัก";
      ok = false;
    }

    // --- validate province ---
    if (!province.trim()) {
      next.province = "กรุณาเลือกจังหวัด";
      ok = false;
    }

    setErrors(next);
    return {
      ok,
      firstError: next.carType || next.carLicense || next.province || "",
    };
  };

  const handleSubmit = async () => {
    try {
      if (!customerData?.id) {
        await Swal.fire({
          icon: "error",
          title: "ไม่พบลูกค้า",
          text: "กรุณาลองใหม่อีกครั้ง",
          customClass: { container: "my-swal" },
        });
        return;
      }

      // ตรวจสอบฟอร์ม
      const { ok, firstError } = validate();
      if (!ok) {
        await Swal.fire({
          icon: "warning",
          title: "ข้อมูลไม่ถูกต้อง",
          text: firstError || "กรุณาตรวจสอบข้อมูลอีกครั้ง",
          customClass: { container: "my-swal" },
        });
        return;
      }

      setSaving(true);

      // รถใหม่
      const newCar = {
        type: carType.trim(),
        license: carLicense,
        province: province.trim(),
        customerId: customerData.id,
      };

      // รวมกับ cars เดิมอย่างปลอดภัย
      const updatedCars = [...(customerData?.cars || []), newCar];

      // อัปเดตครั้งเดียว
      const payload = { cars: updatedCars };
      const res = await services.updateCustomer(payload, customerData.id, {
        includeMeta: false,
      });

      // หา car ที่เพิ่งบันทึกกลับจาก response (ถ้ามี)
      const updatedCustomer = res?.data?.data || res?.data;
      const savedCar =
        updatedCustomer?.cars?.find(
          (c) =>
            c?.license === newCar.license &&
            c?.type === newCar.type &&
            c?.province === newCar.province
        ) || newCar;

      await Swal.fire({
        icon: "success",
        title: isEdit ? "แก้ไขรถสำเร็จ" : "เพิ่มรถสำเร็จ",
        customClass: { container: "my-swal" },
      });

      onSuccess?.(savedCar); // ส่งกลับให้ parent อัปเดตลิสต์ทันที
      resetForm();
      onClose?.();
    } catch (err) {
      console.error("Car save failed:", err);
      await Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถบันทึกรถได้ กรุณาลองใหม่",
        customClass: { container: "my-swal" },
      });
      setSaving(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle className="bg-white p-4 border-b border-gray-100">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                <DirectionsCarIcon />
             </div>
             <div>
                <h2 className="text-xl font-bold text-gray-800">
                    {isEdit ? "แก้ไขข้อมูลรถ" : "เพิ่มข้อมูลรถ"}
                </h2>
                <p className="text-sm text-gray-500 font-normal">
                    กรอกข้อมูลรถของลูกค้าเพื่อใช้ในการออกใบชั่ง
                </p>
             </div>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors"
          >
            <CloseIcon />
          </button>
        </div>
      </DialogTitle>

      <DialogContent className="p-0">
        <div className="p-6 space-y-5">
            {/* ประเภทรถ */}
            <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    <ClassIcon fontSize="small" className="text-gray-400" />
                    ประเภทรถ <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                    <select
                        value={carType}
                        onChange={(e) => {
                            setCarType(e.target.value);
                            if (errors.carType) setErrors((p) => ({ ...p, carType: "" }));
                        }}
                        className={`w-full appearance-none bg-gray-50 border rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all
                            ${errors.carType ? 'border-red-300 focus:ring-red-200' : 'border-gray-200'}
                        `}
                    >
                        <option value="" disabled>-- เลือกประเภทรถ --</option>
                        <option value="เดินมา">เดินมา</option>
                        <option value="รถกระบะ">รถกระบะ</option>
                        <option value="รถเก๋ง">รถเก๋ง</option>
                        <option value="รถพ่วงข้าง">รถพ่วงข้าง</option>
                        <option value="รถ6ล้อ">รถ6ล้อ</option>
                        <option value="รถ10ล้อ">รถ10ล้อ</option>
                    </select>
                     <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                        <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                </div>
                {errors.carType && <p className="text-xs text-red-500 mt-1">{errors.carType}</p>}
            </div>

            {/* ถ้าไม่ใช่เดินมา ให้แสดงฟอร์มทะเบียนและจังหวัด */}
            {carType !== "เดินมา" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-in-down">
                    {/* ทะเบียนรถ */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                            <DirectionsCarIcon fontSize="small" className="text-gray-400" />
                            ทะเบียนรถ <span className="text-red-500">*</span>
                        </label>
                         <input
                            type="text"
                            value={carLicense}
                            onChange={(e) => {
                                setCarLicense(e.target.value);
                                if (errors.carLicense) setErrors((p) => ({ ...p, carLicense: "" }));
                            }}
                            placeholder="ตัวอย่าง 1กข1234"
                            className={`w-full bg-gray-50 border rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium
                                ${errors.carLicense ? 'border-red-300 focus:ring-red-200' : 'border-gray-200'}
                            `}
                        />
                         {errors.carLicense ? (
                            <p className="text-xs text-red-500 mt-1">{errors.carLicense}</p>
                         ) : (
                            <p className="text-xs text-gray-400">กรอกอย่างน้อย 4 หลัก</p>
                         )}
                    </div>

                    {/* จังหวัด */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                            <LocationOnIcon fontSize="small" className="text-gray-400" />
                            จังหวัด <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <select
                                value={province}
                                onChange={(e) => {
                                    setProvince(e.target.value);
                                    if (errors.province) setErrors((p) => ({ ...p, province: "" }));
                                }}
                                className={`w-full appearance-none bg-gray-50 border rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all
                                    ${errors.province ? 'border-red-300 focus:ring-red-200' : 'border-gray-200'}
                                `}
                            >
                                <option value="" disabled>-- เลือกจังหวัด --</option>
                                {provinceList.map((name) => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                             <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                                <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                            </div>
                        </div>
                        {errors.province && <p className="text-xs text-red-500 mt-1">{errors.province}</p>}
                    </div>
                </div>
            )}
        </div>
      </DialogContent>

      <DialogActions className="p-4 border-t border-gray-100 bg-gray-50">
        <div className="flex gap-3 w-full justify-end">
            <button
            onClick={handleClose}
            className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-100 transition-all text-sm"
            disabled={saving}
            >
            ยกเลิก
            </button>
            <button
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
            className="px-8 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all active:scale-95 disabled:opacity-50 disabled:shadow-none text-sm"
            >
            {saving ? (
                <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    กำลังบันทึก...
                </span>
            ) : (
                isEdit ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"
            )}
            </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default CarFormDialog;
